import { memo } from 'react';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { BIMElement } from '../core/types';
import { useBIMStore } from '../state/useBIMStore';
import { useAppStore } from '../state/store';
import { useAnnotationScaleFactor } from '../hooks/useViewScale';
import { useAnnotationInk } from '../hooks/useThemeInk';

/**
 * 2D plan symbols for view-marker elements (Elevation / Section / Camera /
 * Callout), matching BIM's drafting appearance:
 * - Elevation: circle + direction needle + view name label
 * - Section:   cut line + end ticks + direction arrow + label
 * - Camera:    frustum wedge + target cross + label
 * - Callout:   dashed crop rectangle + leader + bubble label
 *
 * Markers are annotations: their paper size is constant and the world size
 * follows the active view scale (useAnnotationScaleFactor).
 */

const SELECTED = '#00aaff';
const CIRCLE_SEGMENTS = 48;

function circlePoints(cx: number, cy: number, r: number, z: number): THREE.Vector3[] {
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= CIRCLE_SEGMENTS; i++) {
        const a = (i / CIRCLE_SEGMENTS) * Math.PI * 2;
        pts.push(new THREE.Vector3(cx + Math.cos(a) * r, cy + Math.sin(a) * r, z));
    }
    return pts;
}

interface SymbolCommon {
    color: string;
    s: number;          // annotation scale factor
    lineWidth: number;  // thin-lines aware
}

/** BIM elevation symbol: circle, direction needle with arrow head, label. */
export function ElevationSymbol({ position, rotation, label, color, s, lineWidth }: SymbolCommon & {
    position: [number, number, number];
    rotation: number;
    label: string;
}) {
    // Look direction: +Y rotated around Z by `rotation` (matches ElevationTool)
    const dir = new THREE.Vector2(-Math.sin(rotation), Math.cos(rotation));
    const r = 0.45 * s;
    const [x, y, z] = position;
    const cz = z + 0.05;

    const tip = new THREE.Vector3(x + dir.x * r * 0.75, y + dir.y * r * 0.75, cz + 0.001);
    const back = new THREE.Vector3(x - dir.x * r * 0.75, y - dir.y * r * 0.75, cz + 0.001);
    // Arrow head wings at the tip
    const left = new THREE.Vector2(-dir.y, dir.x);
    const w1 = new THREE.Vector3(
        tip.x - dir.x * 0.14 * s + left.x * 0.08 * s,
        tip.y - dir.y * 0.14 * s + left.y * 0.08 * s,
        cz + 0.001,
    );
    const w2 = new THREE.Vector3(
        tip.x - dir.x * 0.14 * s - left.x * 0.08 * s,
        tip.y - dir.y * 0.14 * s - left.y * 0.08 * s,
        cz + 0.001,
    );

    // Datum cross ticks inside the circle (perpendicular pair at 45 deg)
    const tick = 0.12 * s;
    const cross = new THREE.Vector2(-dir.y, dir.x);
    const c1 = new THREE.Vector3(x - cross.x * tick, y - cross.y * tick, cz + 0.001);
    const c2 = new THREE.Vector3(x + cross.x * tick, y + cross.y * tick, cz + 0.001);

    const labelPos = new THREE.Vector3(x - dir.x * (r + 0.35 * s), y - dir.y * (r + 0.35 * s), cz + 0.002);

    return (
        <group>
            <Line points={circlePoints(x, y, r, cz)} color={color} lineWidth={lineWidth} />
            <Line points={[back, tip]} color={color} lineWidth={lineWidth} />
            <Line points={[w1, tip, w2]} color={color} lineWidth={lineWidth} />
            <Line points={[c1, c2]} color={color} lineWidth={lineWidth} />
            <mesh position={[x, y, cz + 0.001]}>
                <circleGeometry args={[0.045 * s, 16]} />
                <meshBasicMaterial color={color} />
            </mesh>
            <Text
                position={labelPos}
                fontSize={0.25 * s}
                color={color}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.015 * s}
                outlineColor={color === '#ffffff' ? '#000000' : '#ffffff'}
            >
                {label}
            </Text>
        </group>
    );
}

/** BIM section symbol: cut line, end ticks, direction arrows, label. */
export function SectionSymbol({ start, end, normal, label, color, s, lineWidth }: SymbolCommon & {
    start: [number, number, number];
    end: [number, number, number];
    normal: [number, number, number];
    label: string;
}) {
    const z = start[2] + 0.05;
    const a = new THREE.Vector3(start[0], start[1], z);
    const b = new THREE.Vector3(end[0], end[1], z);
    const n = new THREE.Vector3(normal[0], normal[1], 0).normalize();
    const lineDir = b.clone().sub(a).normalize();

    const tick = 0.18 * s;
    const tickA1 = a.clone().add(lineDir.clone().multiplyScalar(tick));
    const tickA2 = a.clone().sub(lineDir.clone().multiplyScalar(tick));
    const tickB1 = b.clone().add(lineDir.clone().multiplyScalar(tick));
    const tickB2 = b.clone().sub(lineDir.clone().multiplyScalar(tick));

    // Direction arrows at 1/3 and 2/3 along the line, pointing to the view side
    const arrowLen = 0.35 * s;
    const arrows = [1 / 3, 2 / 3].map((t) => {
        const base = a.clone().lerp(b, t);
        const head = base.clone().add(n.clone().multiplyScalar(arrowLen));
        const wing = lineDir.clone().multiplyScalar(0.09 * s);
        return (
            <Line
                key={t}
                points={[base, head, head.clone().sub(wing), head, head.clone().add(wing)]}
                color={color}
                lineWidth={lineWidth}
            />
        );
    });

    const labelPos = a.clone().sub(n.clone().multiplyScalar(0.35 * s)).add(lineDir.clone().multiplyScalar(-0.35 * s));

    return (
        <group>
            <Line points={[a, b]} color={color} lineWidth={Math.max(lineWidth, 1.5)} />
            <Line points={[tickA2, tickA1]} color={color} lineWidth={lineWidth} />
            <Line points={[tickB2, tickB1]} color={color} lineWidth={lineWidth} />
            {arrows}
            <Text
                position={labelPos}
                fontSize={0.25 * s}
                color={color}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.015 * s}
                outlineColor={color === '#ffffff' ? '#000000' : '#ffffff'}
            >
                {label}
            </Text>
        </group>
    );
}

/** BIM camera symbol: body box, frustum wedge, dashed sight line, target. */
export function CameraSymbol({ position, target, label, color, s, lineWidth }: SymbolCommon & {
    position: [number, number, number];
    target: [number, number, number];
    label: string;
}) {
    const eye = new THREE.Vector3(position[0], position[1], position[2] + 0.05);
    const tgt = new THREE.Vector3(target[0], target[1], position[2] + 0.05);
    const dir = tgt.clone().sub(eye);
    const dist = dir.length();
    if (dist < 0.001) return null;
    dir.normalize();
    const left = new THREE.Vector3(-dir.y, dir.x, 0);

    const bodyW = 0.14 * s;
    const bodyL = 0.3 * s;
    const f = bodyL / 2;
    const corners = [
        eye.clone().add(dir.clone().multiplyScalar(f)).add(left.clone().multiplyScalar(bodyW)),
        eye.clone().add(dir.clone().multiplyScalar(f)).sub(left.clone().multiplyScalar(bodyW)),
        eye.clone().sub(dir.clone().multiplyScalar(f)).sub(left.clone().multiplyScalar(bodyW)),
        eye.clone().sub(dir.clone().multiplyScalar(f)).add(left.clone().multiplyScalar(bodyW)),
    ];
    const outline = [...corners, corners[0]];
    const wedgeDepth = Math.min(dist * 0.35, 1.2 * s);
    const spread = 0.5 * s;
    const wl = eye.clone().add(dir.clone().multiplyScalar(f)).add(left.clone().multiplyScalar(bodyW));
    const wr = eye.clone().add(dir.clone().multiplyScalar(f)).sub(left.clone().multiplyScalar(bodyW));
    const fl = eye.clone().add(dir.clone().multiplyScalar(f + wedgeDepth)).add(left.clone().multiplyScalar(spread));
    const fr = eye.clone().add(dir.clone().multiplyScalar(f + wedgeDepth)).sub(left.clone().multiplyScalar(spread));

    const cross = 0.12 * s;
    const labelPos = eye.clone().sub(left.clone().multiplyScalar(0.55 * s));

    return (
        <group>
            <Line points={outline} color={color} lineWidth={lineWidth} />
            <Line points={[wl, fl, fr, wr]} color={color} lineWidth={lineWidth} />
            <Line points={[eye, tgt]} color={color} lineWidth={lineWidth} dashed dashSize={0.15 * s} gapSize={0.1 * s} />
            <Line points={[tgt.clone().sub(new THREE.Vector3(cross, 0, 0)), tgt.clone().add(new THREE.Vector3(cross, 0, 0))]} color={color} lineWidth={lineWidth} />
            <Line points={[tgt.clone().sub(new THREE.Vector3(0, cross, 0)), tgt.clone().add(new THREE.Vector3(0, cross, 0))]} color={color} lineWidth={lineWidth} />
            <Text
                position={labelPos}
                fontSize={0.25 * s}
                color={color}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.015 * s}
                outlineColor={color === '#ffffff' ? '#000000' : '#ffffff'}
            >
                {label}
            </Text>
        </group>
    );
}

/** BIM callout symbol: dashed crop rectangle + leader + bubble label. */
export function CalloutSymbol({ rect, label, color, s, lineWidth }: SymbolCommon & {
    rect: { x: number; y: number; width: number; height: number; z?: number };
    label: string;
}) {
    const { x, y, width, height } = rect;
    const z = (rect.z ?? 0) + 0.05;
    const box = [
        new THREE.Vector3(x, y, z),
        new THREE.Vector3(x + width, y, z),
        new THREE.Vector3(x + width, y + height, z),
        new THREE.Vector3(x, y + height, z),
        new THREE.Vector3(x, y, z),
    ];
    const bubbleR = 0.3 * s;
    const leftMid = new THREE.Vector3(x, y + height / 2, z);
    const bubbleCenter = leftMid.clone().sub(new THREE.Vector3(bubbleR + 0.45 * s, 0, 0));
    const leaderStart = bubbleCenter.clone().add(new THREE.Vector3(bubbleR, 0, 0));

    return (
        <group>
            <Line points={box} color={color} lineWidth={lineWidth} dashed dashSize={0.2 * s} gapSize={0.1 * s} />
            <Line points={[leaderStart, leftMid]} color={color} lineWidth={lineWidth} />
            <Line points={circlePoints(bubbleCenter.x, bubbleCenter.y, bubbleR, z + 0.001)} color={color} lineWidth={lineWidth} />
            <Text
                position={new THREE.Vector3(bubbleCenter.x, bubbleCenter.y, z + 0.002)}
                fontSize={0.22 * s}
                color={color}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.015 * s}
                outlineColor={color === '#ffffff' ? '#000000' : '#ffffff'}
            >
                {label}
            </Text>
        </group>
    );
}

interface ViewMarkerRendererProps {
    element: BIMElement;
    isSelected: boolean;
    /** Markers only draw in top-down plan-family views (BIM behavior). */
    inPlanView: boolean;
}

/**
 * Routes a `category: 'Views'` element to the matching 2D plan symbol.
 */
const ViewMarkerRenderer = memo(function ViewMarkerRenderer({ element, isSelected, inPlanView }: ViewMarkerRendererProps) {
    const s = useAnnotationScaleFactor();
    const ink = useAnnotationInk();
    const thinLines = useAppStore((state) => state.view.thinLines);

    // Single click selects the marker (properties palette); double click
    // opens the linked view — the BIM marker interaction contract.
    // Only active while the Select tool is on so drawing tools keep their
    // normal click-through behavior over markers.
    const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
        if (e.button !== 0) return;
        if (String(useAppStore.getState().activeTool).toLowerCase() !== 'select') return;
        e.stopPropagation();
        useAppStore.getState().select([element.id]);
        useBIMStore.getState().setSelection([element.id]);
    };
    const handleDoubleClick = (e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        const viewId = (element as any).geometryData?.viewId as string | undefined;
        if (viewId && useBIMStore.getState().project.views.has(viewId)) {
            useBIMStore.getState().setActiveView(viewId);
        }
    };

    const geom = (element as any).geometryData;

    // The symbol bodies themselves are line/geometry primitives without event
    // handlers; this invisible disc gives the whole marker a reliable hit area
    // anchored at the marker's own position.
    const hitAnchor = (): [number, number, number] => {
        switch (element.type) {
            case 'Elevation Marker':
                return [geom?.position?.[0] ?? 0, geom?.position?.[1] ?? 0, (geom?.position?.[2] ?? 0) + 0.05];
            case 'Section Marker':
                return [((geom?.start?.[0] ?? 0) + (geom?.end?.[0] ?? 0)) / 2, ((geom?.start?.[1] ?? 0) + (geom?.end?.[1] ?? 0)) / 2, (geom?.start?.[2] ?? 0) + 0.05];
            case 'Camera Marker':
                return [geom?.position?.[0] ?? 0, geom?.position?.[1] ?? 0, (geom?.position?.[2] ?? 0) + 0.05];
            case 'Callout Marker':
                return [geom?.rect?.x ?? 0, geom?.rect?.y ?? 0, (geom?.rect?.z ?? 0) + 0.05];
            default:
                return [0, 0, 0.05];
        }
    };

    if (!inPlanView || !geom) return null;

    const hitArea = (
        <mesh
            position={hitAnchor()}
            visible={false}
            onPointerDown={handlePointerDown}
            onDoubleClick={handleDoubleClick}
        >
            <circleGeometry args={[1.6, 24]} />
            <meshBasicMaterial transparent opacity={0} />
        </mesh>
    );

    const color = isSelected ? SELECTED : ink;
    const lineWidth = thinLines ? 0.75 : 1.5;
    const label = String(element.parameters?.['View Name'] ?? element.name ?? '');

    switch (element.type) {
        case 'Elevation Marker':
            return (
                <group userData={{ id: element.id }}>
                    {hitArea}
                    <ElevationSymbol
                        position={geom.position as [number, number, number]}
                        rotation={geom.rotation as number}
                        label={label}
                        color={color} s={s} lineWidth={lineWidth}
                    />
                </group>
            );
        case 'Section Marker':
            return (
                <group userData={{ id: element.id }}>
                    {hitArea}
                    <SectionSymbol
                        start={geom.start as [number, number, number]}
                        end={geom.end as [number, number, number]}
                        normal={geom.normal as [number, number, number] ?? [0, 1, 0]}
                        label={label}
                        color={color} s={s} lineWidth={lineWidth}
                    />
                </group>
            );
        case 'Camera Marker':
            return (
                <group userData={{ id: element.id }}>
                    {hitArea}
                    <CameraSymbol
                        position={geom.position as [number, number, number]}
                        target={geom.target as [number, number, number]}
                        label={label}
                        color={color} s={s} lineWidth={lineWidth}
                    />
                </group>
            );
        case 'Callout Marker':
            return (
                <group userData={{ id: element.id }}>
                    {hitArea}
                    <CalloutSymbol
                        rect={geom.rect as { x: number; y: number; width: number; height: number }}
                        label={label}
                        color={color} s={s} lineWidth={lineWidth}
                    />
                </group>
            );
        default:
            return null;
    }
});

export default ViewMarkerRenderer;
